import emit from '../../../common/events';
import { ProductCardData } from '../../product-card/product-card-display';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { getAnalyticsSafeData, getUserDataForUserAnalytics } from '../../user-profile/user-profile.selectors';
import { getAppFlow } from '../../user-profile/utils/user-profile.util';
import { getProductDetailsForAnalytics } from '../../../common/utils/product-util';
import { WishList, WishLists } from '../interfaces/wish-list-items.interface';
import { mapWishListItemToProductCardData } from '../mapping/wish-list-product-card.mapping';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { SEG_LOWER } from '../../../common/utils/util';
import { buttonLabelToAnalyticsEvent } from './wish-list.util';
import { mapWishListToWishListAnalyticsObject } from '../mapping/wish-list-analytics-mapping';

export class WishListAnalytics {
  /**
   * Emits create wish list click events.
   *
   * @param element
   * @param wishListId
   * @param wishListName
   * @param user
   * @param label
   * @param cancel
   */
  createWishList(element: Element, wishListId: string, wishListName: string, user: UserProfile, label: string, cancel = false): void {
    emit({
      element: element,
      name: 'click',
      detail: {
        eventName: `mhecomm-wish-list-create${cancel ? '-cancel' : ''}`,
        comp: 'wish-list',
        wishListId: wishListId,
        wishListName: wishListName,
        user: getAnalyticsSafeData(user),
        url: window.location.href,
        label,
      },
    });
  }

  /**
   * Emits edit wish list click events.
   *
   * @param element
   * @param wishListId
   * @param wishListName
   * @param user
   * @param label
   * @param cancel
   */
  editWishList(element: Element, wishListId: number, wishListName: string, user: UserProfile, label: string, cancel = false): void {
    emit({
      element: element,
      name: 'click',
      detail: {
        eventName: `mhecomm-wish-list-edit${cancel ? '-cancel' : ''}`,
        comp: 'wish-list',
        wishListId: wishListId,
        wishListName: wishListName,
        user: getAnalyticsSafeData(user),
        url: window.location.href,
        label,
      },
    });
  }

  /**
   * Emits delete wish list click events.
   *
   * @param element
   * @param wishListId
   * @param wishListName
   * @param user
   * @param label
   * @param cancel
   */
  deleteWishList(element: Element, wishListId: number, wishListName: string, user: UserProfile, label: string, cancel = false): void {
    emit({
      element: element,
      name: 'click',
      detail: {
        eventName: `mhecomm-wish-list-delete${cancel ? '-cancel' : ''}`,
        comp: 'wish-list',
        wishListId: wishListId,
        wishListName: wishListName,
        user: getAnalyticsSafeData(user),
        url: window.location.href,
        label,
      },
      bubbles: false,
      composed: false,
    });
  }

  /**
   * Emits review wishlist items event
   *
   * @param element
   * @param wishLists
   * @param wishListItemCommentMap
   * @param user
   * @param product
   * @param sourceComponent
   */
  updateProductWishLists(
    element: Element,
    wishLists: WishList[],
    wishListItemCommentMap: Record<string, string>,
    user: UserProfile,
    product: ProductCardData,
    sourceComponent: string
  ): void {
    emit({
      element: element,
      name: 'view',
      detail: {
        eventName: `mhecomm-view-product-wish-lists`,
        comp: sourceComponent,
        wishLists: wishLists.map((wishList) => ({
          wishListId: wishList.wishlist_id,
          wishListName: wishList.name,
          comment: wishListItemCommentMap[wishList.wishlist_id] ?? '',
        })),
        user: getAnalyticsSafeData(user),
        productDetail: getProductDetailsForAnalytics(product, SEG_LOWER),
        url: window.location.href,
        label: 'View Wish List Products'
      },
    });
  }

  /**
   * Emits add item to wish list event
   *
   * @param element
   * @param wishList
   * @param user
   * @param product
   * @param sourceComponent
   */
  addProductToWishList(
    element: Element,
    wishList: WishList,
    user: UserProfile,
    product: ProductCardData,
    sourceComponent = 'wish-list'
  ): void {
    emit({
      element: element,
      name: 'click',
      detail: {
        eventName: `mhecomm-add-product-to-wish-list`,
        comp: sourceComponent,
        wishListId: wishList.wishlist_id,
        wishListName: wishList.name,
        user: getAnalyticsSafeData(user),
        productDetail: getProductDetailsForAnalytics(product, SEG_LOWER),
        url: window.location.href,
        label: 'Add Product to Wish List',
      },
    });
  }

  /**
   * Emits remove item from wish list event
   *
   * @param element
   * @param wishList
   * @param user
   * @param product
   * @param sourceComponent
   */
  removeProductFromWishList(
    element: Element,
    wishList: WishList,
    user: UserProfile,
    product: ProductCardData,
    sourceComponent = 'wish-list'
  ): void {
    emit({
      element: element,
      name: 'click',
      detail: {
        eventName: `mhecomm-remove-product-from-wish-list`,
        comp: sourceComponent,
        wishListId: wishList.wishlist_id,
        wishListName: wishList.name,
        user: getAnalyticsSafeData(user),
        productDetail: getProductDetailsForAnalytics(product, SEG_LOWER),
        url: window.location.href,
        label: 'Remove Product from Wish List',
      },
    });
  }

  /**
   * Emits share wish list click events.
   *
   * @param element
   * @param wishListId
   * @param wishListName
   * @param shareableLink
   * @param user
   */
  shareWishList(
    element: Element,
    wishListId: number,
    wishListName: string,
    shareableLink: string,
    user: UserProfile
  ): void {
    emit({
      element: element,
      name: 'click',
      detail: {
        eventName: 'mhecomm-wish-list-share',
        comp: 'off-canvas',
        shareableLink: shareableLink,
        wishListId: wishListId,
        wishListName: wishListName,
        user: getAnalyticsSafeData(user),
        url: window.location.href,
        label: 'Share Wish List',
      },
    });
  }

  /**
   * Emits wishlists events.
   *
   * @param element
   * @param wishLists
   * @param user
   */
  wishLists(element: Element, wishLists: WishLists, user: UserProfile): void {
    emit({
      element: element,
      name: 'view',
      detail: {
        eventName: 'mhecomm-wish-lists-view',
        wishLists: wishLists.items,
        user: getAnalyticsSafeData(user),
        url: window.location.href,
        label: 'View Wish List',
        comp: 'wish-list',
      },
    });
  }

  /**
   * Emits wishlist items events.
   *
   * @param element
   * @param wishList
   * @param user
   */
  wishListItems(element: Element, wishList: WishList, user: UserProfile): void {
    emit({
      element: element,
      name: 'view',
      detail: {
        eventName: 'mhecomm-wish-list-items-view',
        wishListId: wishList?.wishlist_id,
        wishListName: wishList?.name,
        products: wishList?.wishlist_items?.map((wishListItem) => mapWishListItemToProductCardData(wishListItem)),
        user: getAnalyticsSafeData(user),
        url: window.location.href,
        label: 'View Wish List Items',
        comp: 'wish-list',
      },
    });
  }

  /**
   * Event triggered when the user clicks on the back button to go back to the wish list page.
   *
   * @param element
   * @param backButtonId
   * @param backButtonLabel
   * @param user
   */
  backToMyLists(element: Element, backButtonId: string, backButtonLabel: string, user: UserProfile): void {
    emit({
      element: element,
      name: 'click',
      detail: {
        eventName: `mhecomm-${buttonLabelToAnalyticsEvent(backButtonLabel)}`,
        id: backButtonId,
        label: backButtonLabel,
        comp: 'button',
        user: getAnalyticsSafeData(user),
        appFlow: getAppFlow(),
      },
      dataLayer: true,
    });
  }

  /**
   * Emits wish list comment click events.
   *
   * @param element
   * @param comment
   * @param isbn
   * @param wishListId
   * @param cancel
   */
  comment(element: Element, comment: string, wishListId: number, isbn: string, label: string, cancel = false): void {
    emit({
      element: element,
      name: 'click',
      detail: {
        eventName: `mhecomm-wish-list-comment${cancel ? '-cancel' : '-save'}`,
        ...(comment && { comment }),
        ...(wishListId && { wishListId: wishListId }),
        ...(isbn && { isbn }),
        label,
        comp: 'wish-list',
      },
    });
  }

  /**
   * Emits add all to cart click events.
   *
   * @param element
   * @param products
   * @param wishList
   * @param pageType
   * @param label
   */
  addAllItemsToCart(element: Element, products: ProductCardData[], wishList: WishList, pageType: string, label: string) {
    emit({
      element: element,
      name: 'add-all-to-cart',
      detail: {
        eventName: 'mhecomm-wishlist-add-all-to-cart',
        comp: 'button',
        products: products?.map((product: ProductCardData) => getProductDetailsForAnalytics(product, SEG_LOWER)),
        wishListId: wishList?.wishlist_id,
        wishListName: wishList?.name,
        pageType: pageType,
        user: getUserDataForUserAnalytics(store.getState() as StoreState),
        appFlow: getAppFlow(),
        label,
      },
      dataLayer: true,
    });
  }

  /**
   * Emits user login event from heart icon click.
   *
   * @param product
   * @param element
   */
  captureUserLoginAnalytics(
    product: ProductCardData,
    element: Element,
  ): void {
    emit({
      element: element,
      name: 'click',
      detail: {
        eventName: `mhecomm-user-heart-icon-click-login`,
        productDetail: getProductDetailsForAnalytics(product, SEG_LOWER),
        url: window.location.href,
        label: 'Log-In Heart Icon',
        comp: 'button',
      },
    });
  }

  /**
   * Emits the event listener wish-list-review.
   *
   * @param element
   * @param wishList
   * @param id
   */
  wishListReview(
    element: Element,
    wishList: WishList,
    id: string,
  ): void {
    emit({
      element: element,
      name: 'click',
      detail: {
        eventName: 'wish-list-review',
        wishList: wishList,
        label: wishList?.name,
        id,
      },
      dataLayer: false
    })
  }

  /**
   * Emits when the user clicks the wishlist on the print-catalogue off canvas page
   *
   * @param element
   * @param wishList
   */
  wishListItemsOffCanvasView(
    element: Element,
    wishList: WishList,
  ): void {
    emit({
      element: element,
      name: `view`,
      detail: {
        eventName: 'wish-list-items-off-canvas-view',
        wishList: mapWishListToWishListAnalyticsObject(wishList),
        url: window.location.href,
        label: wishList?.name,
        comp: 'wish-list',
      },
    });
  }
}
